/**
 * Creates props to make a non-interactive element keyboard accessible
 * Adds onClick, onKeyDown, tabIndex, role, and optional aria-label attributes
 *
 * @param {Function} clickHandler - The function to call when element is clicked or activated with keyboard
 * @param {Object} options - Configuration options
 * @param {boolean} [options.disabled=false] - Whether the element is disabled
 * @param {string} [options.role='button'] - ARIA role for the element
 * @param {string} [options.ariaLabel] - Optional ARIA label for screen readers
 * @param {boolean} [options.stopPropagationOnChildren=false] - If true, only trigger onClick when clicking directly on the
 *   element (not children). If false (default), clicks on children will bubble up and trigger the handler. Set to true when
 *   child elements have their own independent click handlers.
 * @returns {Object} Props object to spread onto the element
 *
 * @example
 * // Basic usage - clicks on the div or its children trigger the handler
 * <div {...makeKeyboardAccessible(() => handleClick())}>
 *   Click me
 * </div>
 *
 * @example
 * // With disabled state
 * <div {...makeKeyboardAccessible(() => handleEdit(), { disabled: isReadOnly })}>
 *   Edit
 * </div>
 *
 * @example
 * // With custom role
 * <div {...makeKeyboardAccessible(() => toggleMenu(), { role: 'menuitem' })}>
 *   Menu Item
 * </div>
 *
 * @example
 * // With aria-label for screen readers (use comma for natural pause)
 * <div {...makeKeyboardAccessible(() => toggleGrading(), {
 *   ariaLabel: 'AI Gradable, John Smith'
 * })}>
 *   <Toggle />
 * </div>
 *
 * @example
 * // Wrapper with decorative children - clicks anywhere trigger the handler (default)
 * <div {...makeKeyboardAccessible(() => handleClick(), {
 *   ariaLabel: 'View details'
 * })}>
 *   <Icon name='view' />
 *   <span>Details</span>
 * </div>
 *
 * @example
 * // Wrapper with independent interactive children - only wrapper clicks trigger handler
 * <div {...makeKeyboardAccessible(() => handleToggle(), {
 *   ariaLabel: 'Toggle Setting',
 *   role: 'switch',
 *   stopPropagationOnChildren: true
 * })}>
 *   <Checkbox onChange={handleCheckboxChange} />
 *   <InfoIcon onClick={showInfo} />
 * </div>
 */
export const makeKeyboardAccessible = (clickHandler, options = {}) => {
  const { disabled = false, role = 'button', ariaLabel, stopPropagationOnChildren = false } = options;

  const props = {
    onClick: (event) => {
      if (disabled) {
        return;
      }

      // If stopPropagationOnChildren is true, only trigger if clicking directly on this element
      // This allows child elements to handle their own clicks independently
      if (stopPropagationOnChildren && event.target !== event.currentTarget) {
        return;
      }

      // Otherwise, allow clicks on children to bubble up and trigger the handler
      clickHandler(event);
    },
    onKeyDown: (event) => {
      if ((event.key === 'Enter' || event.key === ' ') && !disabled) {
        event.preventDefault();
        clickHandler(event);
      }
    },
    role,
    tabIndex: disabled ? -1 : 0
  };

  // Only add aria-label if provided
  if (ariaLabel) {
    props['aria-label'] = ariaLabel;
  }

  return props;
};
